import { useQueryClient, type QueryOptions } from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import {
  fetchContractParties,
  queryKeysContract,
  type ContractParty,
  type ContractPartyRole,
} from '~/data/contract';

import { SelectServerDriven } from './SelectServerDriven';
import { createGetItemData, createLoadOptions } from './utils';

const getOptionLabel = ({ name }: ContractParty) => name;

type P = {
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly onChange: (ids: UUID[], parties: ContractParty[]) => void;
  readonly partyRole: ContractPartyRole;
  readonly placeholder?: string;
  readonly value: UUID[];
} & ComponentStyling;

/**
 * MultiSelect for the companies that appear as buyer or seller on contracts
 * the caller can see. Backed by GET /contracts/filter_options/parties, which
 * permission-scopes its results by partyRole.
 */
export const MultiSelectContractParties = ({
  className,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  onChange,
  partyRole,
  placeholder,
  style,
  value,
}: P) => {
  const queryClient = useQueryClient();

  const loadOptions = createLoadOptions<ContractParty>({
    createQueryKey: ({ offset, searchString }) =>
      queryKeysContract.getFilterParties({
        offset,
        partyRole,
        searchString: searchString || undefined,
      }),
    async fetchItems({ offset, searchString }) {
      const { data, hasNextPage } = await fetchContractParties({
        offset,
        partyRole,
        searchString: searchString || undefined,
      });

      return {
        count: data.length,
        data,
        hasNextPage,
      };
    },
    queryClient,
    queryOptions: {
      staleTime: ms('60s'),
    } as Partial<QueryOptions>,
  });

  /**
   * Resolves a selected company id to its display data, e.g. when restoring a
   * saved filter. Uses the endpoint's company_ids hydration mode; ids that are
   * not (or no longer) accessible reject and the select drops the chip.
   */
  const getItemData = createGetItemData<ContractParty>({
    createQueryKey: (companyId) => [
      ...queryKeysContract.getFilterParty({ companyId, partyRole }),
    ],
    async fetchItem(companyId) {
      const { data } = await fetchContractParties({
        companyIds: [companyId],
        partyRole,
      });
      const party = data.find(({ id }) => id === companyId);

      if (!party) {
        throw new Error(`Contract party ${companyId} is not accessible`);
      }

      return party;
    },
    queryClient,
    queryKeyBase: [queryKeysContract.base(), 'filter-parties'],
  });

  const handleChange = (
    newValues: string[] | string,
    data: ContractParty[] | ContractParty,
  ) => {
    onChange([newValues].flat(), [data].flat());
  };

  return (
    <SelectServerDriven<ContractParty>
      className={className}
      getItemData={getItemData}
      getOptionLabel={getOptionLabel}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isItemSelectionAllowed={() => true}
      isMultiSelect={isMultiSelect}
      loadOptions={loadOptions}
      placeholder={placeholder}
      style={style}
      value={value}
      onChange={handleChange}
    />
  );
};
